import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../api/admin';
import { useAuth } from '../../context/AuthContext';

const DashboardCard = ({ title, children, className }) => {
  return (
    <div className={`p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors ${className}`}>
      <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">{title}</h2>
      {children}
    </div>
  );
};

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewPermissionsModalOpen, setIsViewPermissionsModalOpen] = useState(false);
  const [rolePermissions, setRolePermissions] = useState(null);
  const [isRolePermissionsLoading, setIsRolePermissionsLoading] = useState(false);
  const [createRoleForm, setCreateRoleForm] = useState({
    name: '',
    description: ''
  });
  
  const { isSuperuser } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const rolesList = await adminApi.getRoles();
        setRoles(rolesList);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
        setError('Failed to load roles. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchRoles();
  }, []);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCreateRoleForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleCreateRole = async (e) => {
    e.preventDefault();
    
    try {
      const newRole = await adminApi.createRole(createRoleForm);
      
      // Update the roles list
      setRoles([...roles, newRole]);
      
      // Reset form and close modal
      setCreateRoleForm({ name: '', description: '' });
      setIsCreateModalOpen(false);
    } catch (err) {
      console.error('Failed to create role:', err);
      // Display error in the form
    }
  };
  
  const viewRolePermissions = async (role) => {
    setSelectedRole(role);
    setIsRolePermissionsLoading(true);
    
    try {
      const permissions = await adminApi.getRolePermissions(role.id);
      setRolePermissions(permissions);
      setIsViewPermissionsModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch role permissions:', err);
    } finally {
      setIsRolePermissionsLoading(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
        <div className="flex justify-center">
          <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="mt-2 text-center text-ui-light-text-muted dark:text-ui-dark-text-muted">Loading roles...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
        <div className="p-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
          <p className="font-medium">Error:</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Role Management</h1>
        
        {isSuperuser() && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
          >
            Create Role
          </button>
        )}
      </div>
      
      <DashboardCard title="Roles">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
            <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
              <tr>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-muted dark:text-ui-dark-text-muted uppercase">
                  Role Name
                </th>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-muted dark:text-ui-dark-text-muted uppercase">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-muted dark:text-ui-dark-text-muted uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
              {roles.map((role) => (
                <tr key={role.id} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                        {role.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary whitespace-nowrap">
                    {role.description || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                    <button
                      onClick={() => viewRolePermissions(role)}
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                    >
                      View Permissions
                    </button>
                    {isSuperuser() && (
                      <button
                        onClick={() => navigate(`/admin/permissions/${role.id}`)}
                        className="ml-4 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                      >
                        Manage Permissions
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DashboardCard>
      
      {/* Create Role Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-ui-light-card dark:bg-ui-dark-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleCreateRole}>
                <div className="bg-ui-light-card dark:bg-ui-dark-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium leading-6 text-ui-light-text-primary dark:text-ui-dark-text-primary">
                      Create New Role
                    </h3>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                      Role Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={createRoleForm.name}
                      onChange={handleInputChange}
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-ui-light-border dark:border-ui-dark-border rounded-md bg-white dark:bg-ui-dark-hover text-ui-light-text-primary dark:text-ui-dark-text-primary"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="description" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                      Description
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      value={createRoleForm.description}
                      onChange={handleInputChange}
                      rows="3"
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-ui-light-border dark:border-ui-dark-border rounded-md bg-white dark:bg-ui-dark-hover text-ui-light-text-primary dark:text-ui-dark-text-primary"
                    ></textarea>
                  </div>
                </div>
                
                <div className="bg-ui-light-hover dark:bg-ui-dark-hover px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 dark:bg-primary-700 text-base font-medium text-white dark:text-ui-dark-text-primary hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-ui-light-border dark:border-ui-dark-border shadow-sm px-4 py-2 bg-white dark:bg-ui-dark-hover text-base font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* View Role Permissions Modal */}
      {isViewPermissionsModalOpen && selectedRole && rolePermissions && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-ui-light-card dark:bg-ui-dark-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-ui-light-card dark:bg-ui-dark-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="mb-4">
                  <h3 className="text-lg font-medium leading-6 text-ui-light-text-primary dark:text-ui-dark-text-primary">
                    Permissions for Role: {selectedRole.name}
                  </h3>
                  <p className="mt-1 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                    {selectedRole.description || 'No description provided'}
                  </p>
                </div>
                
                {isRolePermissionsLoading ? (
                  <div className="flex justify-center py-4">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <div className="mt-2">
                    <h4 className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">Schema Permissions</h4>
                    {rolePermissions.permissions.schema_permissions.length > 0 ? (
                      <div className="mb-4 overflow-y-auto max-h-40">
                        <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
                          <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
                            <tr>
                              <th scope="col" className="px-3 py-2 text-xs font-medium tracking-wider text-left text-ui-light-text-muted dark:text-ui-dark-text-muted uppercase">
                                Schema
                              </th>
                              <th scope="col" className="px-3 py-2 text-xs font-medium tracking-wider text-left text-ui-light-text-muted dark:text-ui-dark-text-muted uppercase">
                                Permission
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
                            {rolePermissions.permissions.schema_permissions.map((perm, idx) => (
                              <tr key={idx} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary">
                                  {perm.schema_name}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                                  <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                                    perm.permission === 'all' ? 'bg-status-success-light-bg dark:bg-status-success-dark-bg text-status-success-light-text dark:text-status-success-dark-text' : 
                                    perm.permission === 'write' ? 'bg-status-warning-light-bg dark:bg-status-warning-dark-bg text-status-warning-light-text dark:text-status-warning-dark-text' : 
                                    'bg-status-info-light-bg dark:bg-status-info-dark-bg text-status-info-light-text dark:text-status-info-dark-text'
                                  }`}>
                                    {perm.permission}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-ui-light-text-muted dark:text-ui-dark-text-muted mb-4">No schema permissions assigned</p>
                    )}
                    
                    <h4 className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">Table Permissions</h4>
                    {rolePermissions.permissions.table_permissions.length > 0 ? (
                      <div className="overflow-y-auto max-h-40">
                        <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
                          <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
                            <tr>
                              <th scope="col" className="px-3 py-2 text-xs font-medium tracking-wider text-left text-ui-light-text-muted dark:text-ui-dark-text-muted uppercase">
                                Schema
                              </th>
                              <th scope="col" className="px-3 py-2 text-xs font-medium tracking-wider text-left text-ui-light-text-muted dark:text-ui-dark-text-muted uppercase">
                                Table
                              </th>
                              <th scope="col" className="px-3 py-2 text-xs font-medium tracking-wider text-left text-ui-light-text-muted dark:text-ui-dark-text-muted uppercase">
                                Permission
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
                            {rolePermissions.permissions.table_permissions.map((perm, idx) => (
                              <tr key={idx} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary">
                                  {perm.schema_name}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary">
                                  {perm.table_name}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                                  <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                                    perm.permission === 'all' ? 'bg-status-success-light-bg dark:bg-status-success-dark-bg text-status-success-light-text dark:text-status-success-dark-text' : 
                                    perm.permission === 'write' ? 'bg-status-warning-light-bg dark:bg-status-warning-dark-bg text-status-warning-light-text dark:text-status-warning-dark-text' : 
                                    'bg-status-info-light-bg dark:bg-status-info-dark-bg text-status-info-light-text dark:text-status-info-dark-text'
                                  }`}>
                                    {perm.permission}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-ui-light-text-muted dark:text-ui-dark-text-muted">No table permissions assigned</p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="bg-ui-light-hover dark:bg-ui-dark-hover px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {isSuperuser() && (
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/permissions/${selectedRole.id}`)}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 dark:bg-primary-700 text-base font-medium text-white dark:text-ui-dark-text-primary hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  >
                    Manage Permissions
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsViewPermissionsModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-ui-light-border dark:border-ui-dark-border shadow-sm px-4 py-2 bg-white dark:bg-ui-dark-hover text-base font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;